import { useEffect, useState } from "react";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { isAdmin } from "../utils/auth";
import { MONTHS } from "../utils/constants";
import "../styles/shared.css";

const ROLES = [
  { value: "DATA_ENTRY", label: "Veri Giriş" },
  { value: "REVIEWER", label: "Kontrol" },
  { value: "REPORT_VIEWER", label: "Rapor" },
  { value: "ADMIN", label: "Yönetici" },
];

export default function AdminScreen() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [tab, setTab] = useState(admin ? "staff" : "audit");
  const [periods, setPeriods] = useState([]);
  const [staff, setStaff] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditUser, setAuditUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  function load() {
    setLoading(true);
    setError("");
    const requests = [
      client.get("/periods/"),
      client.get("/audit-logs/", { params: { limit: 100, user: auditUser || undefined } }),
    ];
    if (admin) {
      requests.splice(1, 0, client.get("/staff/"));
    }

    Promise.allSettled(requests).then((results) => {
      const [p, s, a] = admin ? results : [results[0], null, results[1]];
      if (p.status === "fulfilled") setPeriods(p.value.data);
      else setError("Dönemler yüklenemedi.");

      if (admin) {
        if (s.status === "fulfilled") setStaff(s.value.data);
        else setError((prev) => prev || "Personel listesi yüklenemedi.");
      }

      if (a.status === "fulfilled") setAuditLogs(a.value.data);
    }).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    client.get("/audit-logs/", { params: { limit: 100, user: auditUser || undefined } })
      .then((res) => setAuditLogs(res.data))
      .catch(() => {});
  }, [auditUser]);

  async function togglePeriod(period, action) {
    setMessage("");
    setError("");
    try {
      await client.post(`/periods/${period.id}/${action}/`);
      setMessage(action === "close" ? "Dönem kapatıldı." : "Dönem yeniden açıldı.");
      load();
    } catch {
      setError("İşlem başarısız.");
    }
  }

  async function updateStaffRole(member, role) {
    setMessage("");
    setError("");
    try {
      await client.patch(`/staff/${member.id}/`, { camasirhane_role: role });
      setMessage("Rol güncellendi.");
      load();
    } catch {
      setError("Rol güncellenemedi.");
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Yönetim</h2>
        <p>Personel rolleri, dönem kapatma ve veri girişi takibi</p>
      </div>

      <div className="toolbar">
        <div className="tab-group">
          {admin && (
            <>
              <button className={`ui-btn ${tab === "staff" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("staff")}>Personel</button>
              <button className={`ui-btn ${tab === "periods" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("periods")}>Dönemler</button>
            </>
          )}
          <button className={`ui-btn ${tab === "audit" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("audit")}>İşlem Geçmişi</button>
        </div>
      </div>

      {message && <p className="status-msg success">{message}</p>}
      {error && <p className="status-msg error">{error}</p>}
      {loading && <p className="status-msg">Yükleniyor...</p>}

      {tab === "staff" && admin && (
        <div className="panel">
          <h2>Çamaşırhane Personeli</h2>
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 0 }}>
            Kullanıcılar HelpDesk üzerinden giriş yapar. Modül erişimi &quot;Modül Erişimi&quot; menüsünden,
            çamaşırhane rolü buradan atanır.
          </p>
          <div className="grid-wrapper">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>E-posta</th>
                  <th>Ad Soyad</th>
                  <th>Rol</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id}>
                    <td className="label-col">{member.email}</td>
                    <td>{member.full_name || "-"}</td>
                    <td>
                      <select
                        className="ui-select"
                        value={member.role || "DATA_ENTRY"}
                        onChange={(e) => updateStaffRole(member, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>{member.is_active ? "Aktif" : "Pasif"}</td>
                  </tr>
                ))}
                {!staff.length && <tr><td colSpan={4}>Modül erişimi olan personel bulunamadı.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="panel">
          <h2>Kim, Ne Zaman, Hangi Veriyi Girdi?</h2>
          <div className="toolbar" style={{ boxShadow: "none", border: "none", padding: 0, marginBottom: 16 }}>
            <select className="ui-select" value={auditUser} onChange={(e) => setAuditUser(e.target.value)}>
              <option value="">Tüm personel</option>
              {staff.map((member) => (
                <option key={member.id} value={member.id}>{member.full_name || member.email}</option>
              ))}
            </select>
          </div>
          <div className="grid-wrapper">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Personel</th>
                  <th>Modül</th>
                  <th>İşlem</th>
                  <th>Kayıt #</th>
                  <th>Detay</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.created_at).toLocaleString("tr-TR")}</td>
                    <td className="label-col">{log.full_name || log.username || "-"}</td>
                    <td>{log.table_label}</td>
                    <td>{log.action_label}</td>
                    <td>{log.record_id}</td>
                    <td style={{ fontSize: 11, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {log.new_value?.slice(0, 80)}
                    </td>
                  </tr>
                ))}
                {!auditLogs.length && <tr><td colSpan={6}>Henüz kayıt yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "periods" && admin && (
        <div className="panel">
          <h2>Dönemler</h2>
          <div className="grid-wrapper">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>Dönem</th>
                  <th>Başlangıç</th>
                  <th>Bitiş</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id}>
                    <td>{MONTHS[p.month - 1]} {p.year}</td>
                    <td>{p.start_date}</td>
                    <td>{p.end_date}</td>
                    <td>
                      <span style={{ color: p.is_closed ? "var(--color-danger)" : "var(--color-success)", fontWeight: 600 }}>
                        {p.is_closed ? "Kapalı" : "Açık"}
                      </span>
                    </td>
                    <td>
                      {p.is_closed ? (
                        <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => togglePeriod(p, "reopen")}>Aç</button>
                      ) : (
                        <button className="ui-btn ui-btn--sm" onClick={() => togglePeriod(p, "close")}>Kapat</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
